// ── THE COVERAGE-LEVEL OPTIMIZER ─────────────────────────────────────────────
//
// Kevin's scope: a toggle, DEFAULT OFF, strictly dollars and cents, underlying
// MPCI only — no SCO, no ECO (the area endorsements already have their own
// trigger history on the same card, and folding them in here would answer a
// different question with the same number).
//
// ⚠ IT IS A LEDGER, NOT A VERDICT, AND THAT IS NOT A STYLE CHOICE.  Measured
// book-wide, 54 of 105 replayable wheat farms net best at the BOTTOM of the
// ladder over the years we can replay — because insurance costs money in the
// years it is not needed, and a six-year record with two loss years cannot say
// otherwise.  A screen that printed "optimal: 50%" off that would be confidently
// wrong in the one direction that costs a grower a claim.  So the years are the
// product, "best on this record" carries its own sample size, and there is no
// score and no badge.  [[feedback_present_data_not_verdicts]]
import React from "react";
import { buildLevelReplay, REFUSAL_TEXT } from "./levelOptimizer.js";
import { levelLabel } from "./levelLadder.js";

const $ = (n) => (n == null ? "—" : (n < 0 ? "-$" : "$") + Math.abs(n).toFixed(2));
const $0 = (n) => (n == null ? "—" : (n < 0 ? "-$" : "$") + Math.abs(n).toFixed(0));

const C = {
  box: { marginTop: 12, border: "1px solid #cfe3cf", borderRadius: 10, background: "#fbfdfb", padding: "11px 13px" },
  h: { fontSize: 12.5, fontWeight: 800, color: "#1f3f1f", marginBottom: 2 },
  sub: { fontSize: 10.5, color: "#5a6a5a", marginBottom: 8, lineHeight: 1.45 },
  th: { fontSize: 9.5, fontWeight: 700, color: "#5a6a5a", textAlign: "right", padding: "3px 5px", whiteSpace: "nowrap" },
  thL: { fontSize: 9.5, fontWeight: 700, color: "#5a6a5a", textAlign: "left", padding: "3px 5px" },
  td: { fontSize: 10.5, textAlign: "right", padding: "2px 5px", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  tdL: { fontSize: 10.5, textAlign: "left", padding: "2px 5px", whiteSpace: "nowrap" },
  note: { fontSize: 10, color: "#60735f", marginTop: 7, lineHeight: 1.45 },
};

export default function LevelOptimizerPanel({
  dbRows, priceRows, county, practice, state, unitStructure, quoteRP, quotePrice,
  incumbentLevel = null, loading = false, error = null,
}) {
  const res = React.useMemo(() => {
    if (loading || error || !dbRows || !priceRows) return null;
    try { return buildLevelReplay({ dbRows, priceRows, county, practice, state, unitStructure, quoteRP, quotePrice }); }
    catch (e) { return { error: (e && e.message) || String(e) }; }
  }, [dbRows, priceRows, county, practice, state, unitStructure, quoteRP, quotePrice, loading, error]);

  if (loading) return <div style={C.box}><div style={C.h}>Coverage level optimizer</div><div style={C.sub}>Loading this farm's yield record…</div></div>;
  if (error) return <div style={C.box}><div style={C.h}>Coverage level optimizer</div><div style={C.sub}>Couldn't load: {String(error)}</div></div>;
  if (!res) return null;
  if (res.error) return (
    <div style={C.box}>
      <div style={C.h}>Coverage level optimizer</div>
      <div style={C.sub}>Not replayed — {res.error}.</div>
    </div>
  );

  const LV = res.levels;
  const yrs = res.years;
  // A ledger with nothing in it is a fact about the database, not an empty table.
  if (!yrs.length) return (
    <div style={C.box}>
      <div style={C.h}>Coverage level optimizer</div>
      <div style={C.sub}>
        No year on this farm's record can be replayed.{" "}
        {Object.entries(res.refusals || {}).map(([k, v]) => `${v} database-year${v === 1 ? "" : "s"}: ${REFUSAL_TEXT[k] || k}`).join("; ")}.
      </div>
    </div>
  );

  return (
    <div style={C.box}>
      <div style={C.h}>
        Coverage level optimizer — what each level would have collected
      </div>
      <div style={C.sub}>
        {res.yearsCovered} crop year{res.yearsCovered === 1 ? "" : "s"} of this farm's own wheat record
        {res.lossYears ? `, ${res.lossYears} of them paying at some level` : ", none of them paying at any level"}
        {" · "}{res.databases} APH database{res.databases === 1 ? "" : "s"}, acre-weighted.
        {" "}Underlying RP only — no SCO or ECO.
        {" "}Guarantee is APH × level × the higher of RMA's projected and harvest price; revenue is the yield actually
        harvested at the harvest price.
        {res.aphExact
          ? " APH reconstructed from ten reported years, which reproduces the AIP's own rate yield exactly."
          : ` APH reconstructed from as few as ${res.aphMinYears} reported years in the thinnest year, so it is an estimate below ten — the per-year depth is shown.`}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #dbe8db" }}>
              <th style={C.thL}>Year</th>
              <th style={C.th}>APH</th>
              <th style={C.th}>Yield</th>
              <th style={C.th}>Proj</th>
              <th style={C.th}>Harv</th>
              {LV.map((L) => (
                <th key={L} style={{ ...C.th, color: incumbentLevel != null && Math.abs(L - incumbentLevel) < 1e-9 ? "#1f5c2e" : C.th.color,
                                      fontWeight: incumbentLevel != null && Math.abs(L - incumbentLevel) < 1e-9 ? 800 : 700 }}>
                  {levelLabel(L)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {yrs.map((y) => (
              <tr key={y.year} style={{ borderBottom: "1px solid #f0f5f0" }}>
                <td style={C.tdL}>
                  {y.year}
                  {!y.aphExact && <span style={{ color: "#a0b0a0", fontSize: 9 }}> ({y.aphMinYears}y)</span>}
                </td>
                <td style={C.td}>{y.aph.toFixed(1)}</td>
                <td style={{ ...C.td, fontWeight: y.actualYield < y.aph * 0.8 ? 700 : 400 }}>{y.actualYield.toFixed(1)}</td>
                <td style={C.td}>{y.projected.toFixed(2)}</td>
                {/* The harvest price is marked where it EXCEEDED the projected one, because
                    that is the provision raising the guarantee — five of eleven NY wheat
                    years, so it is the ordinary case rather than a footnote. */}
                <td style={{ ...C.td, fontWeight: y.harvest > y.projected ? 700 : 400 }}>
                  {y.harvest.toFixed(2)}{y.harvest > y.projected ? "▲" : ""}
                </td>
                {LV.map((L) => {
                  const c = y.levels[L.toFixed(2)];
                  return (
                    <td key={L} style={{ ...C.td, color: c && c.indemnityPerAc > 0 ? "#1f5c2e" : "#c0ccc0",
                                         fontWeight: c && c.indemnityPerAc > 0 ? 700 : 400 }}>
                      {c ? (c.indemnityPerAc > 0 ? $0(c.indemnityPerAc) : "·") : "?"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ ...C.note, marginTop: 4 }}>Indemnity per acre. “·” is a year that paid nothing at that level.</div>

      <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 10 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid #dbe8db" }}>
            <th style={C.thL}>Level</th>
            <th style={C.th}>Years</th>
            <th style={C.th}>Paid in</th>
            <th style={C.th}>Avg indemnity/ac</th>
            <th style={C.th}>Premium/ac</th>
            <th style={C.th}>Net/ac</th>
          </tr>
        </thead>
        <tbody>
          {LV.map((L) => {
            const t = res.totals[L.toFixed(2)];
            if (!t || !t.years) return null;
            const isInc = incumbentLevel != null && Math.abs(L - incumbentLevel) < 1e-9;
            const isBest = res.best && Math.abs(res.best.level - L) < 1e-9;
            return (
              <tr key={L} style={{ borderBottom: "1px solid #f0f5f0", background: isInc ? "#f2f9f2" : "transparent" }}>
                <td style={{ ...C.tdL, fontWeight: isInc ? 800 : 500 }}>
                  {levelLabel(L)}
                  {isInc && <span style={{ color: "#3a6a3a", fontSize: 9.5, fontWeight: 600 }}> current</span>}
                  {isBest && <span style={{ color: "#8a5a00", fontSize: 9.5, fontWeight: 600 }}> best on this record</span>}
                </td>
                <td style={C.td}>{t.years}</td>
                <td style={C.td}>{t.lossYears}</td>
                <td style={C.td}>{$(t.indemnityPerAc)}</td>
                <td style={C.td}>{t.priced ? $(t.premiumPerAc) : "not priced"}</td>
                <td style={{ ...C.td, fontWeight: 700, color: t.netPerAc == null ? "#60735f" : t.netPerAc >= 0 ? "#1f5c2e" : "#8a3a3a" }}>
                  {t.priced ? $(t.netPerAc) : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* ⚠ THE CAVEAT IS NOT OPTIONAL AND IT IS NOT BEHIND A TOGGLE. A reader who
          takes "best on this record" as a recommendation acts on a statement about
          six years. This repo has already recorded what a caveat behind a control
          is worth: the one reader who needs it cannot see it. */}
      <div style={C.note}>
        <b>This is a record, not a recommendation.</b> “Best on this record” is the level that would have
        netted most over the {res.yearsCovered} year{res.yearsCovered === 1 ? "" : "s"} replayed
        {res.lossYears <= 2 ? `, and only ${res.lossYears} of them paid anything at any level` : ""} —
        it is a statement about those years, not a forecast. Insurance costs money in the years it is not needed,
        so a short record favours the cheapest level.
      </div>
      <div style={C.note}>
        Premiums are priced at <b>CY2027 rates and the CY2027 projected price</b> throughout, on the acres
        the AIP reported for each database in each year — so this reads “carry this level next year and have
        a year like that one”, not “what it would have cost back then”.
      </div>
      {!!Object.keys(res.refusals || {}).length && (
        <div style={C.note}>
          Not replayed:{" "}
          {Object.entries(res.refusals).map(([k, v]) => `${v} database-year${v === 1 ? "" : "s"} — ${REFUSAL_TEXT[k] || k}`).join("; ")}.
        </div>
      )}
      {!!(res.priceDisagreements || []).length && (
        <div style={C.note}>
          ⚠ {res.priceDisagreements.length} price cell{res.priceDisagreements.length === 1 ? "" : "s"} disagree across counties
          and were not merged: {res.priceDisagreements.slice(0, 3).join(", ")}.
        </div>
      )}
    </div>
  );
}
